import React, { useState } from "react";
import {
  getCurrentSettlementRange,
  getPreviousSettlementRange,
  nowISODate,
} from "../../utils/driverDeliveries";

type DriverDeliveriesFiltersProps = {
  dateFrom: string;
  dateTo: string;
  addressQuery: string;
  resultCount: number;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  onChangeAddress: (value: string) => void;
  onToday: () => void;
  onCurrentSettlement: () => void;
  onPreviousSettlement: () => void;
  onClear: () => void;
  onDownloadXls: () => Promise<void>;
};

const wrapperStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "22px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "16px",
  minWidth: 0,
};

const fieldWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 700,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const inputStyle: React.CSSProperties = {
  minHeight: "44px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 12px",
  fontSize: "14px",
  width: "100%",
  minWidth: 0,
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  minWidth: 0,
};

const secondaryButtonStyle: React.CSSProperties = {
  minHeight: "44px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
};

const primaryButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  background: "var(--primary-soft)",
};

const quickFilterButtonStyle = (
  isActive: boolean
): React.CSSProperties => ({
  ...secondaryButtonStyle,
  ...(isActive
    ? {
      background: "var(--primary)",
      border: "1px solid var(--primary)",
      color: "#151515",
      boxShadow: "0 6px 18px rgba(245, 184, 0, 0.18)",
    }
    : {}),
});

const activeFilterStyle: React.CSSProperties = {
  gridColumn: "1 / -1",
  minHeight: "42px",
  borderRadius: "14px",
  border: "1px solid var(--primary)",
  background: "var(--primary-soft)",
  padding: "10px 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
};

const activeFilterTextStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  color: "var(--text-primary)",
  fontSize: "13px",
  fontWeight: 700,
};

const activeFilterDotStyle: React.CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background: "var(--primary)",
  boxShadow: "0 0 0 4px var(--primary-soft)",
};

const resultCountStyle: React.CSSProperties = {
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 600,
};

const DriverDeliveriesFilters = ({
  dateFrom,
  dateTo,
  addressQuery,
  resultCount,
  onChangeFrom,
  onChangeTo,
  onChangeAddress,
  onToday,
  onCurrentSettlement,
  onPreviousSettlement,
  onClear,
  onDownloadXls,
}: DriverDeliveriesFiltersProps) => {
  const [showClearFeedback, setShowClearFeedback] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const today = nowISODate();
  const isToday = dateFrom === today && dateTo === today;
  const currentSettlementRange = getCurrentSettlementRange();
  const previousSettlementRange = getPreviousSettlementRange();

  const isCurrentSettlement =
    dateFrom === currentSettlementRange.from &&
    dateTo === currentSettlementRange.to;

  const isPreviousSettlement =
    dateFrom === previousSettlementRange.from &&
    dateTo === previousSettlementRange.to;

  const formatDate = (value: string) => {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  };

  const periodLabel = isToday
    ? "Jornada actual"
    : isCurrentSettlement
      ? `Liquidación actual: ${formatDate(dateFrom)} al ${formatDate(dateTo)}`
      : isPreviousSettlement
        ? `Última liquidación: ${formatDate(dateFrom)} al ${formatDate(dateTo)}`
        : dateFrom && dateTo
          ? `Período: ${formatDate(dateFrom)} al ${formatDate(dateTo)}`
          : dateFrom
            ? `Desde ${formatDate(dateFrom)}`
            : dateTo
              ? `Hasta ${formatDate(dateTo)}`
              : "Todos los envíos";

  const handleClearFilters = () => {
    setShowClearFeedback(true);
    onClear();

    window.setTimeout(() => {
      setShowClearFeedback(false);
    }, 400);
  };
  const handleDownloadXls = async () => {
    if (isDownloading) return;

    try {
      setIsDownloading(true);
      await onDownloadXls();
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <div className="driver-deliveries-filters" style={wrapperStyle}>
      <div style={fieldWrapStyle}>
        <label style={labelStyle}>Desde</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => onChangeFrom(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div style={fieldWrapStyle}>
        <label style={labelStyle}>Hasta</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => onChangeTo(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div
        className="driver-deliveries-filters__address"
        style={fieldWrapStyle}
      >
        <label style={labelStyle}>Dirección</label>
        <input
          type="text"
          value={addressQuery}
          onChange={(e) => onChangeAddress(e.target.value)}
          style={inputStyle}
          placeholder="Buscar por dirección"
        />
      </div>

      <div
        className="driver-deliveries-filters__actions"
        style={actionsStyle}
      >
        <button style={quickFilterButtonStyle(isToday)} onClick={onToday}>
          Hoy
        </button>
        <button style={quickFilterButtonStyle(isCurrentSettlement)} onClick={onCurrentSettlement}>
          Liquidación actual
        </button>
        <button style={quickFilterButtonStyle(isPreviousSettlement)} onClick={onPreviousSettlement}>
          Última liquidación
        </button>
        <button style={quickFilterButtonStyle(showClearFeedback)} onClick={handleClearFilters}>
          Limpiar Filtros
        </button>
        <button
          style={{
            ...primaryButtonStyle,
            ...(isDownloading
              ? {
                background: "var(--primary)",
                border: "1px solid var(--primary)",
                color: "#151515",
                cursor: "wait",
                opacity: 0.9,
              }
              : {}),
          }}
          onClick={handleDownloadXls}
          disabled={isDownloading}
        >
          {isDownloading ? "Descargando..." : "Descargar XLS"}
        </button>
      </div>
      <div style={activeFilterStyle}>
        <div style={activeFilterTextStyle}>
          <span style={activeFilterDotStyle} />
          <span>{periodLabel}</span>
        </div>

        <span style={resultCountStyle}>
          {resultCount} {resultCount === 1 ? "envío encontrado" : "envíos encontrados"}
        </span>
      </div>
    </div>
  );
};

export default DriverDeliveriesFilters;